import * as t from 'io-ts';
import {isObject} from 'lodash';

import {FrameTypesEnum, makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Constants.
 */

export enum IntegrationSettingsTypesEnum {
  ASANA = 'asana',
  FULLCONTACT = 'fullcontact',
  GITHUB = 'github',
  HIPCHAT = 'hipchat',
  HUBSPOT = 'hubspot',
  INTERCOM = 'intercom',
  JIRA = 'jira',
  MICROSOFT_DYNAMICS = 'microsoftdynamics',
  NICEREPLY = 'nicereply',
  PAGERDUTY = 'pagerduty',
  PIPEDRIVE = 'pipedrive',
  SALESFORCE = 'salesforce2',
  SHOPIFY = 'shopify',
  SLACK = 'slack',
  SMOOCH = 'smooch',
  TRELLO = 'trello',
  WEBHOOKS = 'webhooks',
  GRAMMARLY = 'grammarly',
}
const RIntegrationSettingsTypesEnum = makeEnumRuntimeType(IntegrationSettingsTypesEnum);

/*
 * Runtime type.
 */

const RTopicType = t.readonly(
  t.type({
    image: t.string,
    resourceName: t.string,
  }),
);

const RIntegrationPlugin = t.readonly(
  t.type({
    name: t.string,
    domain: t.string,
    endpoint: t.string,
    authSecret: t.string,
  }),
);

const RConferenceSettings = t.readonly(
  t.type({
    authenticateRoute: t.string,
    createMeetingRoute: t.string,
  }),
);

const RIntegrationRule = t.intersection([
  t.type({
    name: t.string,
  }),
  t.partial({
    altName: t.string,
    tooltip: t.string,
    help: t.type({
      text: t.string,
      link: t.string,
    }),
    params: t.record(t.string, t.any),
    required: t.readonlyArray(t.string), // Keys of params.
    restricted: t.readonlyArray(t.string), // 'global', 'tea', 'tim'...
  }),
]);

const RIntegrationDirty = t.readonly(
  t.type({
    id: t.Integer,
    frameType: t.literal(FrameTypesEnum.INTEGRATION),
    frameId: t.string,
    type: t.string,
    customSettingsType: t.union([RIntegrationSettingsTypesEnum, t.undefined]),
    typeNoAuthUrl: t.string,
    typeUrl: t.string,
    iconsUrl: t.string,
    name: t.string,
    isEnabled: t.boolean,
    isVirtual: t.boolean,
    topicType: t.union([t.undefined, RTopicType]),
    plugin: t.union([t.undefined, RIntegrationPlugin]),
    conferenceSettings: t.union([t.undefined, RConferenceSettings]),
    hasPlugin: t.boolean,
    hasMessageExtensions: t.boolean,
    hasAttachmentExtensions: t.boolean,
    hasSignatureExtensions: t.boolean,
    settings: t.object, // TODO: Replace with "unknown" after updating the Redux typings.
    teammateSettings: t.union([t.object, t.undefined]),
    rules: t.union([
      t.type({
        actions: t.union([t.record(t.string, RIntegrationRule), t.undefined]),
        conditions: t.union([t.record(t.string, RIntegrationRule), t.undefined]),
      }),
      t.undefined,
    ]),
    updatedAt: t.Integer,
  }),
  'integration',
);

const RBaseIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.undefined,
  }),
]);

/*
 * Specific Integration Settings.
 */

const RAsanaIntegrationSettingsDirty = t.type({
  readonly: t.union([
    t.type({
      userId: t.string,
      username: t.string,
    }),
    t.undefined,
  ]),
});

const RAsanaIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.ASANA),
    settings: RAsanaIntegrationSettingsDirty,
  }),
]);

const RFullcontactIntegrationSettingsDirty = t.type({
  apiKey: t.union([t.string, t.undefined]),
});

const RFullcontactIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.FULLCONTACT),
    settings: t.union([RFullcontactIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RGithubIntegrationSettingsDirty = t.type({
  readonly: t.union([
    t.type({
      login: t.union([t.string, t.undefined]),
    }),
    t.undefined,
  ]),
});

const RGithubIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.GITHUB),
    settings: t.union([RGithubIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RGrammarlyIntegrationSettingsDirty = t.type({
  accessToPremiumGrantedAt: t.union([t.number, t.undefined]),
});

const RGrammarlyIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.GRAMMARLY),
    settings: t.union([RGrammarlyIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RHipchatIntegrationSettingsDirty = t.type({
  auth_token: t.union([t.string, t.undefined]),
  default_room_id: t.union([t.string, t.undefined]),
});

const RHipchatIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.HIPCHAT),
    settings: t.union([RHipchatIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RHubspotIntegrationSettingsDirty = t.type({
  readonly: t.union([
    t.type({
      username: t.string,
      loggedIn: t.boolean,
    }),
    t.undefined,
  ]),
});

const RHubspotIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.HUBSPOT),
    settings: RHubspotIntegrationSettingsDirty,
  }),
]);

const RIntercomIntegrationSettingsDirty = t.type({
  readonly: t.union([
    t.type({
      account: t.union([
        t.type({
          user_name: t.union([t.string, t.undefined]),
          app_id: t.union([t.string, t.undefined]),
        }),
        t.undefined,
      ]),
    }),
    t.undefined,
  ]),
});

const RIntercomIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.INTERCOM),
    settings: t.union([RIntercomIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RJiraIntegrationSettingsDirty = t.partial({
  readonly: t.union([
    t.type({
      baseUrl: t.union([t.string, t.undefined]),
      login: t.union([t.string, t.undefined]),
      consumerKey: t.union([t.string, t.undefined]),
      publicKey: t.union([t.string, t.undefined]),
    }),
    t.undefined,
  ]),
  jiraServer: t.union([t.boolean, t.undefined]),
  jiraServerUrl: t.union([t.string, t.undefined]),
});

const RJiraIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.JIRA),
    settings: t.union([RJiraIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RMicrosoftDynamicsIntegrationSettingsDirty = t.type({
  readonly: t.union([
    t.type({
      username: t.string,
      environmentBaseUrl: t.string,
    }),
    t.undefined,
  ]),
});

const RMicrosoftDynamicsIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS),
    settings: RMicrosoftDynamicsIntegrationSettingsDirty,
  }),
]);

const RNicereplyIntegrationSettingsDirty = t.partial({
  org_name: t.union([t.string, t.undefined]),
});

const RNicereplyIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.NICEREPLY),
    settings: t.union([RNicereplyIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RPagerDutyIntegrationSettingsDirty = t.partial({
  readonly: t.union([
    t.type({
      accountName: t.union([t.string, t.undefined]),
    }),
    t.undefined,
  ]),
});

const RPagerDutyIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.PAGERDUTY),
    settings: t.union([RJiraIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RPipedriveIntegrationSettingsDirty = t.partial({
  readonly: t.union([
    t.type({
      expiresAt: t.union([t.number, t.undefined]),
      loggedIn: t.union([t.boolean, t.undefined]),
    }),
    t.undefined,
  ]),
  subdomain: t.union([t.string, t.undefined]),
});

const RPipedriveIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.PIPEDRIVE),
    settings: t.union([RPipedriveIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RSalesforceIntegrationSettingsDirty = t.type({
  readonly: t.union([
    t.type({
      user: t.union([
        t.type({
          name: t.string,
        }),
        t.undefined,
      ]),
    }),
    t.undefined,
  ]),
});

const RSalesforceIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.SALESFORCE),
    settings: RSalesforceIntegrationSettingsDirty,
  }),
]);

const RShopifyIntegrationSettingsStoreDirty = t.type({
  apiDomain: t.union([t.string, t.undefined]),
  apiKey: t.union([t.string, t.undefined]),
  apiPassword: t.union([t.string, t.undefined]),
});

const RShopifyIntegrationSettingsDirty = t.partial({
  stores: t.union([t.readonlyArray(RShopifyIntegrationSettingsStoreDirty), t.undefined]),
});

const RShopifyIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.SHOPIFY),
    settings: t.union([RShopifyIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RSlackIntegrationSettingsDirty = t.partial({
  readonly: t.union([
    t.type({
      team: t.union([
        t.type({
          id: t.string,
          name: t.string,
        }),
        t.undefined,
      ]),
      user: t.union([
        t.type({
          id: t.string,
          name: t.string,
        }),
        t.undefined,
      ]),
    }),
    t.undefined,
  ]),
  interactive: t.union([t.boolean, t.undefined]),
});

const RSlackIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.SLACK),
    settings: t.union([RSlackIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RSmoochIntegrationSettingsDirty = t.partial({
  appSecret: t.union([t.string, t.undefined]),
  keyId: t.union([t.string, t.undefined]),
});

const RSmoochIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.SMOOCH),
    settings: t.union([RSmoochIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RTrelloIntegrationSettingsDirty = t.partial({
  readonly: t.union([
    t.type({
      userId: t.union([t.string, t.undefined]),
      username: t.union([t.string, t.undefined]),
    }),
    t.undefined,
  ]),
});

const RTrelloIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.TRELLO),
    settings: t.union([RTrelloIntegrationSettingsDirty, t.undefined]),
  }),
]);

const RWebhooksIntegrationSettingsDirty = t.type({
  sendFullEvent: t.union([t.boolean, t.undefined]),
});

const RWebhooksIntegrationDirty = t.intersection([
  RIntegrationDirty,
  t.type({
    customSettingsType: t.literal(IntegrationSettingsTypesEnum.WEBHOOKS),
    settings: t.union([RWebhooksIntegrationSettingsDirty, t.undefined]),
  }),
]);

/*
 * Type.
 */

export interface TopicTypeModel extends t.TypeOf<typeof RTopicType> {}
export interface IntegrationPluginModel extends t.TypeOf<typeof RIntegrationPlugin> {}
export interface ConferenceSettingsModel extends t.TypeOf<typeof RConferenceSettings> {}
export interface BaseIntegrationModel extends t.TypeOf<typeof RBaseIntegrationDirty> {}
export interface IntegrationRuleModel extends t.TypeOf<typeof RIntegrationRule> {}
export const RIntegration = t.clean<IntegrationModel>(RIntegrationDirty as any);
export const RIntegrations = t.readonlyArray(RIntegration);

export interface AsanaIntegrationModel extends t.TypeOf<typeof RAsanaIntegrationDirty> {}
export interface AsanaIntegrationSettingsModel extends t.TypeOf<typeof RAsanaIntegrationSettingsDirty> {}
export interface FullcontactIntegrationModel extends t.TypeOf<typeof RFullcontactIntegrationDirty> {}
export interface FullcontactIntegrationSettingsModel
  extends t.TypeOf<typeof RFullcontactIntegrationSettingsDirty> {}
export interface GithubIntegrationModel extends t.TypeOf<typeof RGithubIntegrationDirty> {}
export interface GithubIntegrationSettingsModel extends t.TypeOf<typeof RGithubIntegrationSettingsDirty> {}
export interface GrammarlyIntegrationModel extends t.TypeOf<typeof RGrammarlyIntegrationDirty> {}
export interface GrammarlyIntegrationSettingsModel
  extends t.TypeOf<typeof RGrammarlyIntegrationSettingsDirty> {}
export interface HipchatIntegrationModel extends t.TypeOf<typeof RHipchatIntegrationDirty> {}
export interface HipchatIntegrationSettingsModel extends t.TypeOf<typeof RHipchatIntegrationSettingsDirty> {}
export interface HubspotIntegrationModel extends t.TypeOf<typeof RHubspotIntegrationDirty> {}
export interface IntercomIntegrationModel extends t.TypeOf<typeof RIntercomIntegrationDirty> {}
export interface IntercomIntegrationSettingsModel
  extends t.TypeOf<typeof RIntercomIntegrationSettingsDirty> {}
export interface JiraIntegrationModel extends t.TypeOf<typeof RJiraIntegrationDirty> {}
export interface JiraIntegrationSettingsModel extends t.TypeOf<typeof RJiraIntegrationSettingsDirty> {}
export interface MicrosoftDynamicsIntegrationModel
  extends t.TypeOf<typeof RMicrosoftDynamicsIntegrationDirty> {}
export interface NicereplyIntegrationModel extends t.TypeOf<typeof RNicereplyIntegrationDirty> {}
export interface NicereplyIntegrationSettingsModel
  extends t.TypeOf<typeof RNicereplyIntegrationSettingsDirty> {}
export interface PagerDutyIntegrationModel extends t.TypeOf<typeof RPagerDutyIntegrationDirty> {}
export interface PagerDutyIntegrationSettingsModel
  extends t.TypeOf<typeof RPagerDutyIntegrationSettingsDirty> {}
export interface PipedriveIntegrationModel extends t.TypeOf<typeof RPipedriveIntegrationDirty> {}
export interface PipedriveIntegrationSettingsModel
  extends t.TypeOf<typeof RPipedriveIntegrationSettingsDirty> {}
export interface SalesforceIntegrationModel extends t.TypeOf<typeof RSalesforceIntegrationDirty> {}
export interface ShopifyIntegrationModel extends t.TypeOf<typeof RShopifyIntegrationDirty> {}
export interface ShopifyIntegrationSettingsModel extends t.TypeOf<typeof RShopifyIntegrationSettingsDirty> {}
export interface ShopifyIntegrationSettingsStoreModel
  extends t.TypeOf<typeof RShopifyIntegrationSettingsStoreDirty> {}
export interface SlackIntegrationModel extends t.TypeOf<typeof RSlackIntegrationDirty> {}
export interface SlackIntegrationSettingsModel extends t.TypeOf<typeof RSlackIntegrationSettingsDirty> {}
export interface SmoochIntegrationModel extends t.TypeOf<typeof RSmoochIntegrationDirty> {}
export interface SmoochIntegrationSettingsModel extends t.TypeOf<typeof RSmoochIntegrationSettingsDirty> {}
export interface TrelloIntegrationModel extends t.TypeOf<typeof RTrelloIntegrationDirty> {}
export interface TrelloIntegrationSettingsModel extends t.TypeOf<typeof RTrelloIntegrationSettingsDirty> {}
export interface WebhooksIntegrationModel extends t.TypeOf<typeof RWebhooksIntegrationDirty> {}
export interface WebhooksIntegrationSettingsModel
  extends t.TypeOf<typeof RWebhooksIntegrationSettingsDirty> {}

export type IntegrationModel =
  | BaseIntegrationModel
  | AsanaIntegrationModel
  | FullcontactIntegrationModel
  | GithubIntegrationModel
  | GrammarlyIntegrationModel
  | HipchatIntegrationModel
  | HubspotIntegrationModel
  | IntercomIntegrationModel
  | JiraIntegrationModel
  | MicrosoftDynamicsIntegrationModel
  | NicereplyIntegrationModel
  | PagerDutyIntegrationModel
  | PipedriveIntegrationModel
  | SalesforceIntegrationModel
  | ShopifyIntegrationModel
  | SlackIntegrationModel
  | SmoochIntegrationModel
  | TrelloIntegrationModel
  | WebhooksIntegrationModel;

/*
 * IO methods.
 */

export function parseBootIntegrations(src: any): ReadonlyArray<IntegrationModel> {
  const bootIntegrations = (src && src.map(parseIntegration)) || [];
  const defaultIntegrations = buildDefaultIntegrations();

  return [...bootIntegrations, ...defaultIntegrations];
}

function parseIntegrations(src: any): ReadonlyArray<IntegrationModel> {
  return src.integrations.map((integration: any) => parseIntegration(integration));
}

export function parseIntegration(src: any): IntegrationModel {
  return {
    id: src.id,
    frameType: FrameTypesEnum.INTEGRATION,
    frameId: `integration:${src.id}`,
    type: src.type.id,
    customSettingsType: RIntegrationSettingsTypesEnum.is(src.type.id) ? src.type.id : undefined,
    typeNoAuthUrl: src.type.no_auth_url,
    typeUrl: src.type.url,
    iconsUrl: `${src.type.url}/public/icons/`,
    name: src.type.name,
    isEnabled: src.enabled,
    isVirtual: false,
    topicType: parseTopicType(src),
    plugin: parsePlugin(src),
    conferenceSettings: parseConferenceSettings(src),
    hasPlugin: Boolean(src.type.plugin),
    hasMessageExtensions: parseHasMessageExtensions(src),
    hasAttachmentExtensions: parseHasAttachmentExtensions(src),
    hasSignatureExtensions: parseHasSignatureExtensions(src),
    settings: src.settings || {},
    teammateSettings: src.teammate_settings ?? undefined,
    updatedAt: src.updated_at || 0, // TEMPORARY: Add the property on the server.
    rules: {
      actions: parseIntegrationRules(src.type.rules?.actions),
      conditions: parseIntegrationRules(src.type.rules?.conditions),
    },
  };
}

function parseIntegrationRules(src: any): Record<string, IntegrationRuleModel> {
  if (!src) {
    return {};
  }

  const rules = {
    ...src,
  };

  Object.keys(rules).forEach((key) => {
    rules[key].altName = rules[key].alt_name;
    delete rules[key].alt_name;
  });

  return rules;
}

function parseTopicType(src: any): TopicTypeModel | undefined {
  return {
    image: `${src.type.url}/public/icons/hover.svg`,
    resourceName: src.type.name,
  };
}

function parsePlugin(src: any): IntegrationPluginModel | undefined {
  if (!isObject(src.type.plugin) || !src.secret_key) {
    return undefined;
  }

  return {
    name: src.type.name,
    domain: src.type.plugin.domain,
    endpoint: src.type.plugin.endpoint,
    authSecret: src.secret_key,
  };
}

function parseConferenceSettings(src: any): ConferenceSettingsModel | undefined {
  if (!isObject(src.type.conferenceSettings)) {
    return undefined;
  }

  return {
    authenticateRoute: src.type.conferenceSettings.authenticateRoute,
    createMeetingRoute: src.type.conferenceSettings.createMeetingRoute,
  };
}

// TEMPORARY: This information should be provided by the server.
function parseHasMessageExtensions(src: any) {
  return src.type.id === 'slack';
}

// TEMPORARY: This information should be provided by the server.
const supportsAttachmentAction = ['dropbox', 'googledrive'];
function parseHasAttachmentExtensions(src: any) {
  return supportsAttachmentAction.includes(src.type.id);
}

// TEMPORARY: This information should be provided by the server.
function parseHasSignatureExtensions(src: any) {
  return src.type.id === 'nicereply';
}

/*
 * API type.
 */

export const ApiIntegration = makeApiType('integration', RIntegration, parseIntegration);
export const ApiIntegrations = makeApiType('integrations', RIntegrations, parseIntegrations);

/*
 * Default integrations.
 */

export const contactsIntegrationFrameId = 'integration:0';

/**
 * Build the default integrations:
 * 1. Contact Details.
 * 2. Calendar.
 */
export function buildDefaultIntegrations(): ReadonlyArray<IntegrationModel> {
  return [
    {
      id: 0,
      frameType: FrameTypesEnum.INTEGRATION,
      frameId: contactsIntegrationFrameId,
      type: 'default',
      customSettingsType: undefined,
      typeNoAuthUrl: '/api/1/integration_types/default',
      typeUrl: '/api/1/integration_types/default',
      iconsUrl: '/api/1/integration_types/default/public/icons/contacts-',
      name: 'Contacts',
      isEnabled: true,
      isVirtual: false,
      topicType: undefined,
      plugin: undefined,
      conferenceSettings: undefined,
      hasPlugin: true,
      hasMessageExtensions: false,
      hasAttachmentExtensions: false,
      hasSignatureExtensions: false,
      settings: {},
      teammateSettings: {},
      rules: undefined,
      updatedAt: 0,
    },
  ];
}
